import { trackEvent } from '@/lib/analytics';

/**
 * Guards React against DOM mutations made by in-browser page translators
 * (Chrome translate, 360/QQ browser auto-translate, etc.).
 *
 * Translators replace text nodes with <font> wrappers React doesn't know
 * about; React's next commit then calls removeChild/insertBefore with a
 * node whose parent changed and throws
 *   "Failed to execute 'removeChild' on 'Node': The node to be removed
 *    is not a child of this node."
 * GA (2026-07-13): 27 such app_crash events in 8 days on /editor, 67% from
 * China desktop — i.e. auto-translated sessions. Standard workaround from
 * facebook/react#11538: make the two DOM ops tolerant instead of throwing.
 *
 * The `dom_translation_guard` event fires at most once per op per page load
 * so we can verify in GA that crashes converted into guard hits.
 */
export function installDomTranslationGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) return;
  const proto = Node.prototype as Node & { __laTranslationGuard?: boolean };
  if (proto.__laTranslationGuard) return;
  proto.__laTranslationGuard = true;

  const reported = new Set<string>();
  const report = (op: string) => {
    if (reported.has(op)) return;
    reported.add(op);
    console.warn(`[domTranslationGuard] intercepted ${op} on a foreign node (page translator active?)`);
    trackEvent('dom_translation_guard', { op });
  };

  const originalRemoveChild = proto.removeChild;
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      report('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      report('insertBefore');
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
